import { useEffect, useState, useMemo, useCallback } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { getBibleReadings, getPrayers } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Heart, Cross, Calendar, ChevronDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TranslatedContent from '../components/TranslatedContent';
import usePageBanner from '../hooks/usePageBanner';
import { AutoTranslate } from '../hooks/useAutoTranslate';

// Component to render psalm content with proper stanza formatting and translation
const PsalmContent = ({ content, language, isRTL }) => {
  const text = content?.[language] || content?.sv || '';
  
  if (!text) return null;
  
  // Split by double newlines to get stanzas
  const stanzas = text.split(/\n\n+/);
  const rtlBorderStartStyle = isRTL
    ? { textAlign: 'right', borderRightWidth: '4px', borderRightStyle: 'solid' }
    : { borderLeftWidth: '4px', borderLeftStyle: 'solid' };
  
  // Function to render line with R. colored differently
  const renderLineWithResponse = (line) => {
    // Check if line ends with R. or (R.) or contains " R." or "R. "
    const responseMatch = line.match(/(.*?)(\s*R\.\s*|\s*\(R\.\)\s*)$/);
    const endsWithEllerHalleluja = line.includes('eller Halleluja');
    
    if (responseMatch) {
      // Line ends with R. - color only the R. part
      const [, textPart, responsePart] = responseMatch;
      return (
        <>
          <AutoTranslate>{textPart}</AutoTranslate>
          <span className="font-semibold text-purple-800">{responsePart}</span>
        </>
      );
    }
    
    if (endsWithEllerHalleluja) {
      // Handle "eller Halleluja." case - ensure proper spacing
      const parts = line.split(/(eller Halleluja\.?)/);
      return (
        <>
          {parts.map((part, i) => (
            <span key={`halleluja-${i}-${part.slice(0,10)}`}>
              {part.includes('eller Halleluja') ? (
                <> <AutoTranslate>{part}</AutoTranslate></>
              ) : (
                <AutoTranslate>{part}</AutoTranslate>
              )}
            </span>
          ))}
        </>
      );
    }
    
    return <AutoTranslate>{line}</AutoTranslate>;
  };
  
  return (
    <div className={`space-y-4 ${isRTL ? 'text-right' : 'text-left'}`} style={isRTL ? { textAlign: 'right' } : undefined} dir={isRTL ? 'rtl' : 'ltr'}>
      {stanzas.map((stanza, idx) => {
        const lines = stanza.split('\n').filter(l => l.trim());
        const isMainResponse = lines.length === 1 && lines[0].trim().startsWith('R.');
        
        return (
          <div 
            key={`stanza-${idx}-${stanza.slice(0,15)}`} 
            className={`${isMainResponse ? `font-semibold text-purple-800 ${isRTL ? 'pr-3 rounded-l' : 'pl-3 rounded-r'} border-purple-400 py-1 bg-purple-100/50` : 'text-gray-700 italic'}`}
            style={isMainResponse ? rtlBorderStartStyle : undefined}
          >
            {lines.map((line, lineIdx) => {
              return (
                <p 
                  key={`line-${lineIdx}-${line.slice(0,15)}`} 
                  className={`${lineIdx > 0 ? 'mt-1' : ''}`}
                >
                  {isMainResponse ? (
                    // Main response line (R. ... text) - translate but keep R. colored
                    <>
                      <span className="text-purple-800 font-semibold">R. </span>
                      <AutoTranslate>{line.replace(/^R\.\s*/, '')}</AutoTranslate>
                    </>
                  ) : (
                    renderLineWithResponse(line)
                  )}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// Get ISO week start date (Monday) for a given year and week number
const getWeekStart = (year, week) => {
  // January 4th is always in week 1 (ISO 8601)
  const jan4 = new Date(year, 0, 4);
  // Find Monday of week 1
  const dayOfWeek = jan4.getDay() || 7; // Convert Sunday (0) to 7
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);
  // Add weeks
  const targetMonday = new Date(week1Monday);
  targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
  return targetMonday;
};

// Get 7 days of a week (Monday to Sunday) - returns array of YYYY-MM-DD strings
const getWeekDays = (year, week) => {
  const start = getWeekStart(year, week);
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    // Format as YYYY-MM-DD without timezone conversion
    const y = day.getFullYear();
    const m = String(day.getMonth() + 1).padStart(2, '0');
    const d = String(day.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${d}`);
  }
  return days;
};

const BibleReadingsPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [readings, setReadings] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedReading, setSelectedReading] = useState(null);
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('bible_readings', language);

  const textAlignClass = isRTL ? 'text-right' : 'text-left';
  // Inline styles bypass the global App.css [dir="rtl"] .text-right / .border-r-4 flip rules
  const readingAlignStyle = isRTL ? { textAlign: 'right' } : undefined;
  const rtlBorderStartStyle = isRTL
    ? { textAlign: 'right', borderRightWidth: '4px', borderRightStyle: 'solid' }
    : { borderLeftWidth: '4px', borderLeftStyle: 'solid' };

  // Get locale map for date formatting
  const localeMap = {
    'sv': 'sv-SE', 'en': 'en-US', 'ar': 'ar-SA',
    'pl': 'pl-PL', 'es': 'es-ES', 'fr': 'fr-FR', 'de': 'de-DE'
  };
  const locale = localeMap[language] || 'sv-SE';

  // Get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return { year: d.getFullYear(), week: weekNo };
  };

  // Get week identifier
  const getWeekId = (weekData) => `${weekData.year}-W${weekData.week}`;

  // Format week for display
  const formatWeekLabel = (weekData) => {
    const { year, week } = weekData;
    const weekLabel = language === 'sv' ? 'Vecka' : 'Week';
    return `${weekLabel} ${week}, ${year}`;
  };

  // Normalize date to YYYY-MM-DD format
  const normalizeDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toISOString().split('T')[0];
  };

  const loadData = useCallback(async () => {
    try {
      const [readingsData, prayersData] = await Promise.all([
        getBibleReadings(),
        getPrayers(),
      ]);
      
      // Normalize dates and sort readings
      const normalizedReadings = readingsData.map(r => ({
        ...r,
        date: normalizeDate(r.date)
      }));
      const sortedReadings = normalizedReadings.sort((a, b) => new Date(a.date) - new Date(b.date));
      setReadings(sortedReadings);
      setPrayers(prayersData);

      // Group readings by week
      const weekMap = new Map();
      sortedReadings.forEach(reading => {
        const weekData = getWeekNumber(reading.date);
        const weekId = getWeekId(weekData);
        if (!weekMap.has(weekId)) {
          weekMap.set(weekId, { ...weekData, readings: [] });
        }
        weekMap.get(weekId).readings.push(reading);
      });

      // Sort weeks (most recent first)
      const weeks = Array.from(weekMap.values()).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.week - a.week;
      });

      // Find current week
      const currentWeek = getWeekNumber(new Date());
      const currentWeekId = getWeekId(currentWeek);
      const foundWeek = weeks.find(w => getWeekId(w) === currentWeekId);
      const defaultWeek = foundWeek || (weeks.length > 0 ? weeks[0] : null);
      
      setSelectedWeek(defaultWeek);

      // Find today's reading or first of the week
      if (defaultWeek) {
        const today = new Date().toISOString().split('T')[0];
        const todayReading = defaultWeek.readings.find(r => r.date === today);
        setSelectedReading(todayReading || defaultWeek.readings[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get all weeks that have readings
  const weekOptions = useMemo(() => {
    const weekMap = new Map();
    readings.forEach(reading => {
      const weekData = getWeekNumber(reading.date);
      const weekId = getWeekId(weekData);
      if (!weekMap.has(weekId)) {
        weekMap.set(weekId, { ...weekData, readings: [] });
      }
      weekMap.get(weekId).readings.push(reading);
    });
    return Array.from(weekMap.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.week - a.week;
    });
  }, [readings]);

  // Get readings for selected week as a map by date
  const weekReadingsMap = useMemo(() => {
    const map = {};
    if (selectedWeek) {
      selectedWeek.readings.forEach(r => {
        map[r.date] = r;
      });
    }
    return map;
  }, [selectedWeek]);

  // Get the 7 days of the selected week
  const weekDays = useMemo(() => {
    if (!selectedWeek) return [];
    return getWeekDays(selectedWeek.year, selectedWeek.week);
  }, [selectedWeek]);

  const dailyPrayers = prayers.filter((p) => p.prayer_type === 'daily');
  const papalPrayers = prayers.filter((p) => p.prayer_type === 'papal');
  const rosaryPrayers = prayers.filter((p) => p.prayer_type === 'rosary');

  // Check if a date is today (using local date comparison)
  const isToday = (dateStr) => {
    // Get today's date in YYYY-MM-DD format using local timezone
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const todayStr = `${year}-${month}-${day}`;
    return dateStr === todayStr;
  };

  // Format date for grid display (using locale-aware day name)
  const formatDayCard = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayName = date.toLocaleDateString(locale, { weekday: 'short' });
    const dayNum = date.getDate();
    return { dayName, dayNum };
  };

  // Handle week change
  const handleWeekChange = (weekId) => {
    const week = weekOptions.find(w => getWeekId(w) === weekId);
    setSelectedWeek(week);
    if (week && week.readings.length > 0) {
      // Select today if in this week, otherwise first day
      const today = new Date().toISOString().split('T')[0];
      const todayReading = week.readings.find(r => r.date === today);
      setSelectedReading(todayReading || week.readings[0]);
    } else {
      setSelectedReading(null);
    }
  };

  // Handle day selection
  const handleDaySelect = (dateStr) => {
    const reading = weekReadingsMap[dateStr];
    if (reading) {
      setSelectedReading(reading);
    }
  };

  // Get current week for comparison
  const currentWeekData = getWeekNumber(new Date());
  const isCurrentWeek = selectedWeek && 
    selectedWeek.year === currentWeekData.year && 
    selectedWeek.week === currentWeekData.week;

  // Render the reading content
  const ReadingContent = ({ reading }) => {
    if (!reading) return null;
    
    const readingDate = new Date(reading.date);
    const dayName = readingDate.toLocaleDateString(locale, { weekday: 'long' });
    const dateStr = readingDate.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' });

    return (
      <Card className="border-0 shadow-xl card-hover mt-6">
        <CardContent className="p-6">
          <div className={`flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={textAlignClass}>
              <h3 className="text-2xl font-serif font-bold text-gray-900 capitalize">
                {dayName}
              </h3>
              <p className="text-sm text-gray-600">{dateStr}</p>
              {isToday(reading.date) && (
                <span className="inline-block mt-2 px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                  <AutoTranslate>Idag</AutoTranslate>
                </span>
              )}
            </div>
            <BookOpen className="w-8 h-8 text-blue-600" />
          </div>

          <div className="space-y-6">
            {/* First Reading */}
            <div className={`bg-blue-50 rounded-lg p-5 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={readingAlignStyle}>
              <h4 className={`font-semibold text-blue-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`w-2 h-2 bg-blue-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                {t('firstReading')}
              </h4>
              {reading.first_reading_reference && (
                <p className="text-sm text-blue-700 font-medium mb-3 italic">
                  {reading.first_reading_reference}
                </p>
              )}
              <TranslatedContent
                content={reading.first_reading}
                as="p"
                className="text-gray-800 leading-relaxed whitespace-pre-wrap"
              />
            </div>

            {/* Psalm - with stanza formatting */}
            <div className={`bg-purple-50 rounded-lg p-5 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={readingAlignStyle}>
              <h4 className={`font-semibold text-purple-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`w-2 h-2 bg-purple-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                {t('psalm')}
              </h4>
              {reading.psalm_reference && (
                <p className="text-sm text-purple-700 font-medium mb-3 italic">
                  {reading.psalm_reference}
                </p>
              )}
              <PsalmContent content={reading.psalm} language={language} isRTL={isRTL} />
            </div>

            {/* Second Reading */}
            {reading.second_reading && reading.second_reading.sv && (
              <div className={`bg-green-50 rounded-lg p-5 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={readingAlignStyle}>
                <h4 className={`font-semibold text-green-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className={`w-2 h-2 bg-green-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                  {t('secondReading')}
                </h4>
                {reading.second_reading_reference && (
                  <p className="text-sm text-green-700 font-medium mb-3 italic">
                    {reading.second_reading_reference}
                  </p>
                )}
                <TranslatedContent
                  content={reading.second_reading}
                  as="p"
                  className="text-gray-800 leading-relaxed whitespace-pre-wrap"
                />
              </div>
            )}

            {/* Vers före evangeliet (Verse before the Gospel) */}
            {reading.vers_fore_evangeliet && reading.vers_fore_evangeliet.sv && (
              <div className={`bg-gradient-to-r ${isRTL ? 'from-rose-50 to-pink-50' : 'from-pink-50 to-rose-50'} rounded-lg p-4 border-pink-500 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={rtlBorderStartStyle}>
                <h4 className={`font-semibold text-pink-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className={`w-2 h-2 bg-pink-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                  <AutoTranslate>Vers före evangeliet</AutoTranslate>
                </h4>
                {reading.vers_fore_evangeliet_reference && (
                  <p className="text-sm text-pink-700 font-medium mb-2 italic">
                    {reading.vers_fore_evangeliet_reference}
                  </p>
                )}
                {reading.vers_fore_evangeliet_instruction && (
                  <p className="text-xs text-gray-500 italic mb-3">
                    {reading.vers_fore_evangeliet_instruction}
                  </p>
                )}
                <TranslatedContent
                  content={reading.vers_fore_evangeliet}
                  as="div"
                  className="text-gray-800 leading-relaxed whitespace-pre-wrap"
                />
              </div>
            )}

            {/* Alleluia (fallback if vers_fore_evangeliet not present) */}
            {!reading.vers_fore_evangeliet && reading.alleluia && reading.alleluia.sv && (
              <div className={`bg-gradient-to-r ${isRTL ? 'from-rose-50 to-pink-50' : 'from-pink-50 to-rose-50'} rounded-lg p-4 border-pink-500 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={rtlBorderStartStyle}>
                <h4 className={`font-semibold text-pink-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className={`w-2 h-2 bg-pink-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                  {t('alleluiaVerse')}
                </h4>
                {reading.alleluia_reference && (
                  <p className="text-sm text-pink-700 font-medium mb-2 italic">
                    {reading.alleluia_reference}
                  </p>
                )}
                <TranslatedContent
                  content={reading.alleluia}
                  as="p"
                  className="text-gray-800 leading-relaxed whitespace-pre-wrap"
                />
              </div>
            )}

            {/* Gospel */}
            <div className={`bg-yellow-50 rounded-lg p-5 border-2 border-yellow-300 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={readingAlignStyle}>
              <h4 className={`font-semibold text-yellow-900 mb-2 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`w-2 h-2 bg-yellow-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                {t('gospel')}
              </h4>
              {reading.gospel_reference && (
                <p className="text-sm text-yellow-800 font-medium mb-3 italic">
                  {reading.gospel_reference}
                </p>
              )}
              <TranslatedContent
                content={reading.gospel}
                as="p"
                className="text-gray-900 leading-relaxed font-medium whitespace-pre-wrap"
              />
            </div>

            {/* Reflection */}
            {reading.reflection && reading.reflection.sv && (
              <div className={`bg-gradient-to-r ${isRTL ? 'from-orange-50 to-amber-50' : 'from-amber-50 to-orange-50'} rounded-lg p-5 ${textAlignClass}`} dir={isRTL ? 'rtl' : 'ltr'} style={readingAlignStyle}>
                <h4 className={`font-semibold text-orange-900 mb-3 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className={`w-2 h-2 bg-orange-600 rounded-full ${isRTL ? 'ml-2' : 'mr-2'}`}></span>
                  {t('reflection')}
                </h4>
                <TranslatedContent
                  content={reading.reflection}
                  as="p"
                  className="text-gray-800 leading-relaxed whitespace-pre-wrap"
                />
              </div>
            )}
          </div>

          {/* Copyright */}
          <div className="mt-6 pt-4 border-t border-gray-300">
            <p className="text-xs text-gray-500 text-center italic">
              {t('bibleCopyright')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen themed-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage}
          alt="Bible Readings"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-black/40 to-black/70' : 'from-black/70 to-black/40'} flex items-center`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full ${textAlignClass}`}>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle || <AutoTranslate>Dagliga Bibelläsningar</AutoTranslate>}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle || <AutoTranslate>Skrift och bön för din andliga resa</AutoTranslate>}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12" data-testid="bible-section" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${textAlignClass}`}>
          <Tabs defaultValue="readings" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="readings" data-testid="readings-tab">
                <BookOpen className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('bible')}
              </TabsTrigger>
              <TabsTrigger value="prayers" data-testid="prayers-tab">
                <Heart className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('prayers')}
              </TabsTrigger>
              <TabsTrigger value="rosary" data-testid="rosary-tab">
                <Cross className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('rosary')}
              </TabsTrigger>
            </TabsList>

            {/* Today's Readings */}
            <TabsContent value="readings">
              {loading ? (
                <div className="text-center py-20">
                  <p className="text-gray-600">{t('loadingReadings')}</p>
                </div>
              ) : weekOptions.length === 0 ? (
                <Card className="p-12 text-center border-0 shadow-xl card-hover">
                  <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                    {t('noReadingsAvailable')}
                  </h3>
                  <p className="text-gray-600">
                    {t('readingsComingSoon')}
                  </p>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Week Selector */}
                  <Card className="border-0 shadow-xl card-hover">
                    <CardContent className="p-4 sm:p-6">
                      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar className="w-5 h-5 text-blue-600" />
                          <h3 className="text-lg font-serif font-bold text-gray-900">
                            {t('selectWeek') || <AutoTranslate>Välj vecka</AutoTranslate>}
                          </h3>
                        </div>
                        <div className="relative">
                          <select
                            value={selectedWeek ? getWeekId(selectedWeek) : ''}
                            onChange={(e) => handleWeekChange(e.target.value)}
                            className={`appearance-none w-full sm:w-auto bg-white border-2 border-gray-300 rounded-lg px-4 py-2 ${isRTL ? 'pl-10' : 'pr-10'} text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer`}
                            data-testid="week-selector"
                          >
                            {weekOptions.map((week) => {
                              const weekId = getWeekId(week);
                              const isCurrent = week.year === currentWeekData.year && week.week === currentWeekData.week;
                              return (
                                <option key={weekId} value={weekId}>
                                  {formatWeekLabel(week)} {isCurrent ? `(${t('currentWeek') || 'Denna vecka'})` : ''}
                                </option>
                              );
                            })}
                          </select>
                          <ChevronDown className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none`} />
                        </div>
                      </div>
                      
                      {isCurrentWeek && (
                        <div className={`mt-4 p-3 bg-green-50 border-green-500 rounded`} style={rtlBorderStartStyle}>
                          <p className="text-sm text-green-800 font-medium">
                            📅 {t('viewingCurrentWeek') || <AutoTranslate>Du visar denna vecka</AutoTranslate>}
                          </p>
                        </div>
                      )}

                      {/* Day Selection Row - 7 days only */}
                      {selectedWeek && (
                        <div className="mt-6">
                          <div className="grid grid-cols-7 gap-1 sm:gap-2">
                            {weekDays.map((dateStr) => {
                              const { dayName, dayNum } = formatDayCard(dateStr);
                              const hasReading = !!weekReadingsMap[dateStr];
                              const isSelected = selectedReading?.date === dateStr;
                              const todayDate = isToday(dateStr);
                              
                              return (
                                <button
                                  key={dateStr}
                                  onClick={() => handleDaySelect(dateStr)}
                                  disabled={!hasReading}
                                  className={`
                                    p-2 sm:p-3 rounded-lg transition-all text-center
                                    ${!hasReading 
                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50' 
                                      : isSelected 
                                        ? 'bg-blue-600 text-white shadow-lg scale-105' 
                                        : todayDate 
                                          ? 'bg-green-100 text-green-800 hover:bg-green-200 ring-2 ring-green-500' 
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }
                                  `}
                                  data-testid={`day-${dateStr}`}
                                >
                                  <div className="text-xs font-medium uppercase opacity-75">
                                    {dayName}
                                  </div>
                                  <div className="text-lg sm:text-xl font-bold">
                                    {dayNum}
                                  </div>
                                  {todayDate && hasReading && !isSelected && (
                                    <div className="text-[10px] font-semibold">
                                      <AutoTranslate>Idag</AutoTranslate>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Selected Reading Content */}
                  {selectedReading ? (
                    <ReadingContent reading={selectedReading} />
                  ) : selectedWeek && (
                    <Card className="p-8 text-center border-0 shadow-xl card-hover">
                      <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">
                        <AutoTranslate>Välj en dag för att se läsningen</AutoTranslate>
                      </p>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Prayers */}
            <TabsContent value="prayers">
              <div className="space-y-6">
                {dailyPrayers.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">{t('dailyPrayers')}</h2>
                    <div className="space-y-4">
                      {dailyPrayers.map((prayer) => (
                        <Card key={prayer.id} className="border-0 shadow-xl card-hover">
                          <CardContent className="p-6">
                            <TranslatedContent
                              content={prayer.title}
                              as="h3"
                              className="text-xl font-serif font-bold text-gray-900 mb-3"
                            />
                            <TranslatedContent
                              content={prayer.content}
                              as="p"
                              className="text-gray-700 leading-relaxed whitespace-pre-line"
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {papalPrayers.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">{t('papalIntentions')}</h2>
                    <div className="space-y-4">
                      {papalPrayers.map((prayer) => (
                        <Card key={prayer.id} className="border-0 shadow-xl card-hover bg-gradient-to-br from-yellow-50 to-white">
                          <CardContent className="p-6">
                            <TranslatedContent
                              content={prayer.title}
                              as="h3"
                              className="text-xl font-serif font-bold text-gray-900 mb-3"
                            />
                            <TranslatedContent
                              content={prayer.content}
                              as="p"
                              className="text-gray-700 leading-relaxed whitespace-pre-line"
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {dailyPrayers.length === 0 && papalPrayers.length === 0 && (
                  <Card className="p-12 text-center border-0 shadow-xl card-hover">
                    <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                      {t('noPrayersAvailable')}
                    </h3>
                    <p className="text-gray-600">
                      {t('prayersComingSoon')}
                    </p>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Rosary */}
            <TabsContent value="rosary">
              <div className="space-y-6">
                {rosaryPrayers.length > 0 ? (
                  rosaryPrayers.map((prayer) => (
                    <Card key={prayer.id} className="border-0 shadow-xl card-hover">
                      <CardContent className="p-6">
                        <TranslatedContent
                          content={prayer.title}
                          as="h3"
                          className="text-xl font-serif font-bold text-gray-900 mb-3"
                        />
                        <TranslatedContent
                          content={prayer.content}
                          as="p"
                          className="text-gray-700 leading-relaxed whitespace-pre-line"
                        />
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card className="p-12 text-center border-0 shadow-xl card-hover">
                    <Cross className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                      {t('rosary')}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t('rosaryPrayersAndMysteries')}
                    </p>
                    <div className={`p-6 bg-blue-50 rounded-lg ${isRTL ? 'text-right' : 'text-left'} max-w-2xl mx-auto`}>
                      <h4 className="font-bold text-gray-900 mb-2">
                        {t('howToPrayRosary')}
                      </h4>
                      <ol className={`list-decimal ${isRTL ? 'list-inside-rtl' : 'list-inside'} space-y-2 text-sm text-gray-700`} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                        <li>{t('rosaryStep1')}</li>
                        <li>{t('rosaryStep2')}</li>
                        <li>{t('rosaryStep3')}</li>
                        <li>{t('rosaryStep4')}</li>
                        <li>{t('rosaryStep5')}</li>
                        <li>{t('rosaryStep6')}</li>
                        <li>{t('rosaryStep7')}</li>
                        <li>{t('rosaryStep8')}</li>
                      </ol>
                    </div>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BibleReadingsPage;
